import React from 'react';

import { SelectTextField } from '@lumapps/combobox/components/SelectTextField';
import type { SingleSelectTextFieldProps } from '@lumapps/combobox/components/SelectTextField/SelectTextField';
import { useCatalogCategories } from '@lumapps/learning-catalog/hooks/useCatalogCategories';
import { CATALOG } from '@lumapps/learning-catalog/keys';
import { CategoryLegacy, FlatenedCategory } from '@lumapps/learning/types';
import { useTranslate } from '@lumapps/translations';

export type CatalogCategoryPickerFieldProps = Partial<
    Omit<SingleSelectTextFieldProps<FlatenedCategory>, 'selectionType' | 'getOptionId' | 'getOptionName' | 'options'>
> & {
    locale: string;
    /** selected catalog category id. */
    valueId?: string;
};
/**
 * Select field in order to select learning catalog categories
 *
 * @family Pickers
 * @param CatalogCategoryPickerFieldProps
 * @returns CatalogCategoryPickerField
 */
export const CatalogCategoryPickerField: React.FC<CatalogCategoryPickerFieldProps> = ({
    locale,
    valueId,
    value,
    ...props
}) => {
    const { translateKey } = useTranslate();
    const [searchValue, setSearchValue] = React.useState<string | undefined>(undefined);
    const { baseLoadingStatus, data } = useCatalogCategories(locale);

    const isVisible = React.useCallback(
        (choice: FlatenedCategory) => {
            return !searchValue || choice.categoryData.title.match(new RegExp(searchValue, 'i'));
        },
        [searchValue],
    );

    const flattenCategories = (
        categories: CategoryLegacy[],
        parentCode?: string,
        depth: number = 0,
    ): FlatenedCategory[] => {
        return categories.reduce((acc, category) => {
            const newCategory: FlatenedCategory = {
                categoryData: category,
                parentCode: parentCode ?? '',
                depth,
            };

            return category.children?.length
                ? [...acc, newCategory, ...flattenCategories(category.children, category.code, depth + 1)]
                : [...acc, newCategory];
        }, [] as FlatenedCategory[]);
    };

    const flatCatalogCategories = data ? flattenCategories(data) : [];

    return (
        <SelectTextField<FlatenedCategory>
            selectionType="single"
            autoFilter={false}
            getOptionName={(option) => option.categoryData.title}
            getOptionId={(option) => option.categoryData.code}
            helper={translateKey(CATALOG.SELECT_CATEGORY_HELPER)}
            options={searchValue ? flatCatalogCategories?.filter(isVisible) : flatCatalogCategories}
            status={baseLoadingStatus}
            onSearch={setSearchValue}
            renderOption={(option) => (
                <SelectTextField.Option>
                    {'-- '.repeat(option.depth) + option.categoryData.title}
                </SelectTextField.Option>
            )}
            {...props}
            value={
                valueId
                    ? flatCatalogCategories?.find((option) => option.categoryData.code === valueId)
                    : (value as FlatenedCategory)
            }
        />
    );
};
